import type { PostHog } from "posthog-js";

export interface UserSegment {
  id: string;
  name: string;
  description: string;
  criteria: Record<string, unknown>;
}

export interface FunnelStep {
  event: string;
  name: string;
  description: string;
  properties?: Record<string, unknown>;
}

export interface CohortMetrics {
  cohortId: string;
  cohortName: string;
  userCount: number;
  period: string;
  metrics: Record<string, number>;
}

// User Segments for Climbing App
export const USER_SEGMENTS: UserSegment[] = [
  {
    id: "new_users",
    name: "New Users",
    description: "Users who signed up in the last 7 days",
    criteria: {
      signup_date: "last_7_days",
      profile_completed: "any",
    },
  },
  {
    id: "active_climbers",
    name: "Active Climbers",
    description: "Users who view multiple models/areas and use 3D viewer",
    criteria: {
      model_views: ">= 5",
      "3d_viewer_sessions": ">= 2",
      last_activity: "last_14_days",
    },
  },
  {
    id: "power_users",
    name: "Power Users",
    description:
      "Users who use advanced features like measurements and multiple map types",
    criteria: {
      "3d_viewer_measurements": ">= 1",
      map_type_changes: ">= 2",
      session_duration: ">= 300000", // 5 minutes
      feature_diversity: ">= 4", // Used at least 4 different features
    },
  },
  {
    id: "content_creators",
    name: "Content Creators",
    description: "Users who edit/create content",
    criteria: {
      edit_actions: ">= 1",
      admin_access: true,
    },
  },
  {
    id: "map_focused",
    name: "Map-Focused Users",
    description: "Users who primarily use map features",
    criteria: {
      map_sessions: ">= 3",
      map_searches: ">= 2",
      model_views: "< 3",
    },
  },
  {
    id: "mobile_users",
    name: "Mobile Users",
    description: "Users primarily accessing via mobile",
    criteria: {
      device_type: "mobile",
      sessions: ">= 2",
    },
  },
  {
    id: "at_risk",
    name: "At Risk Users",
    description: "Users who haven't been active recently",
    criteria: {
      last_activity: "7_to_14_days_ago",
      total_sessions: ">= 2",
    },
  },
  {
    id: "dormant",
    name: "Dormant Users",
    description: "Users who haven't been active for 2+ weeks",
    criteria: {
      last_activity: "> 14_days_ago",
      total_sessions: ">= 1",
    },
  },
];

// Funnel Definitions
export const FUNNELS = {
  onboarding: {
    name: "User Onboarding Funnel",
    description: "From signup to first meaningful interaction",
    steps: [
      {
        event: "user_signed_up",
        name: "Sign Up",
        description: "User creates account",
      },
      {
        event: "profile_completed",
        name: "Complete Profile",
        description: "User completes onboarding",
      },
      {
        event: "page_viewed",
        name: "First Page View",
        description: "User navigates to main app",
        properties: { page_path: "/" },
      },
      {
        event: "map_session_started",
        name: "First Map Interaction",
        description: "User starts exploring map",
      },
      {
        event: "model_page_viewed",
        name: "First model View",
        description: "User views their first model/area",
      },
    ] as FunnelStep[],
  },

  engagement: {
    name: "User Engagement Funnel",
    description: "From casual browsing to active usage",
    steps: [
      {
        event: "page_viewed",
        name: "Page View",
        description: "User visits any page",
      },
      {
        event: "map_marker_clicked",
        name: "Marker Click",
        description: "User clicks on map marker",
      },
      {
        event: "model_page_viewed",
        name: "model View",
        description: "User views model details",
      },
      {
        event: "3d_viewer_session_started",
        name: "3D Viewer Used",
        description: "User engages with 3D viewer",
      },
      {
        event: "3d_viewer_measurement_started",
        name: "Advanced Feature Used",
        description: "User uses measurement tool",
      },
    ] as FunnelStep[],
  },

  content_discovery: {
    name: "Content Discovery Funnel",
    description: "How users find and engage with content",
    steps: [
      {
        event: "map_search_query",
        name: "Search Query",
        description: "User searches for content",
      },
      {
        event: "map_search_results",
        name: "Search Results",
        description: "User sees search results",
      },
      {
        event: "map_search_selected",
        name: "Search Selection",
        description: "User selects search result",
      },
      {
        event: "model_page_viewed",
        name: "Content View",
        description: "User views content details",
      },
      {
        event: "model_route_selected",
        name: "Route Selection",
        description: "User selects specific route",
      },
    ] as FunnelStep[],
  },

  feature_adoption: {
    name: "Feature Adoption Funnel",
    description: "How users discover and adopt advanced features",
    steps: [
      {
        event: "model_page_viewed",
        name: "model View",
        description: "User views model page",
      },
      {
        event: "3d_viewer_session_started",
        name: "3D Viewer Started",
        description: "User opens 3D viewer",
      },
      {
        event: "3d_viewer_route_selected",
        name: "Route Selected in 3D",
        description: "User selects route in 3D viewer",
      },
      {
        event: "3d_viewer_measurement_started",
        name: "Measurement Tool Used",
        description: "User uses measurement tool",
      },
      {
        event: "3d_viewer_measurement_completed",
        name: "Measurement Completed",
        description: "User completes measurement",
      },
    ] as FunnelStep[],
  },
};

// Cohort Definitions
export const COHORTS = {
  weekly_signups: {
    name: "Weekly Signup Cohorts",
    description: "Users grouped by signup week",
    period: "week",
    baseEvent: "user_signed_up",
  },
  monthly_signups: {
    name: "Monthly Signup Cohorts",
    description: "Users grouped by signup month",
    period: "month",
    baseEvent: "user_signed_up",
  },
  feature_adopters: {
    name: "Feature Adopter Cohorts",
    description: "Users grouped by first feature adoption",
    period: "week",
    baseEvent: "3d_viewer_session_started",
  },
};

// User Segmentation Helper Functions
export class UserSegmentationHelper {
  private posthog: PostHog;

  constructor(posthog: PostHog) {
    this.posthog = posthog;
  }

  // Identify user segment based on their activity
  identifyUserSegment(userProperties: Record<string, unknown>): string[] {
    const segments: string[] = [];

    // Check each segment criteria
    USER_SEGMENTS.forEach((segment) => {
      if (this.matchesSegmentCriteria(userProperties, segment.criteria)) {
        segments.push(segment.id);
      }
    });

    return segments;
  }

  // Update user properties with segment information
  updateUserSegments(
    userId: string,
    userProperties: Record<string, unknown>
  ): void {
    const segments = this.identifyUserSegment(userProperties);

    this.posthog.identify(userId, {
      ...userProperties,
      user_segments: segments,
      segment_count: segments.length,
      is_power_user: segments.includes("power_users"),
      is_active_climber: segments.includes("active_climbers"),
      is_at_risk: segments.includes("at_risk"),
      is_content_creator: segments.includes("content_creators"),
      segmentation_updated_at: new Date().toISOString(),
    });
  }

  // Track segment-specific events
  trackSegmentEvent(
    eventName: string,
    properties: Record<string, unknown>
  ): void {
    const segments = this.identifyUserSegment(properties);

    this.posthog.capture(eventName, {
      ...properties,
      user_segments: segments,
      segment_count: segments.length,
    });
  }

  private matchesSegmentCriteria(
    userProperties: Record<string, unknown>,
    criteria: Record<string, unknown>
  ): boolean {
    // This is a simplified matching logic
    // In a real implementation, you'd want more sophisticated matching
    return Object.entries(criteria).every(([key, value]) => {
      const userValue = userProperties[key];

      if (typeof value === "string") {
        if (value.startsWith(">=")) {
          const threshold = parseInt(value.substring(2), 10);
          return userValue >= threshold;
        }
        if (value.startsWith(">")) {
          const threshold = parseInt(value.substring(1), 10);
          return userValue > threshold;
        }
        if (value.startsWith("<")) {
          const threshold = parseInt(value.substring(1), 10);
          return userValue < threshold;
        }
        return userValue === value;
      }

      if (typeof value === "boolean") {
        return userValue === value;
      }

      return userValue === value;
    });
  }
}

// Retention Analysis Helper
export class RetentionAnalyzer {
  private posthog: PostHog;

  constructor(posthog: PostHog) {
    this.posthog = posthog;
  }

  // Track retention milestones
  trackRetentionMilestone(
    userId: string,
    milestone: string,
    properties: Record<string, unknown>
  ): void {
    this.posthog.capture("retention_milestone", {
      user_id: userId,
      milestone,
      days_since_signup: properties.days_since_signup,
      total_sessions: properties.total_sessions,
      features_used: properties.features_used,
      ...properties,
    });
  }

  // Track user return events
  trackUserReturn(userId: string, properties: Record<string, unknown>): void {
    this.posthog.capture("user_returned", {
      user_id: userId,
      days_since_last_visit: properties.days_since_last_visit,
      session_count: properties.session_count,
      features_used_this_session: properties.features_used_this_session,
      ...properties,
    });
  }
}

// Engagement Scoring
export class EngagementScorer {
  private posthog: PostHog;

  constructor(posthog: PostHog) {
    this.posthog = posthog;
  }

  // Calculate engagement score based on user activity
  calculateEngagementScore(userActivity: Record<string, unknown>): number {
    let score = 0;

    // Base activity points
    score += (userActivity.page_views || 0) * 1;
    score += (userActivity.map_sessions || 0) * 5;
    score += (userActivity.model_views || 0) * 3;
    score += (userActivity.search_queries || 0) * 2;

    // Advanced feature usage
    score += (userActivity.viewer_3d_sessions || 0) * 10;
    score += (userActivity.measurements_completed || 0) * 15;
    score += (userActivity.fullscreen_usage || 0) * 5;

    // Social/creation activity
    score += (userActivity.edit_actions || 0) * 20;
    score += (userActivity.profile_updates || 0) * 5;

    // Frequency bonus
    if (userActivity.session_count > 5) score += 25;
    if (userActivity.session_count > 10) score += 50;

    // Consistency bonus (multiple days)
    if (userActivity.active_days > 3) score += 30;
    if (userActivity.active_days > 7) score += 60;

    return Math.min(score, 1000); // Cap at 1000
  }

  // Track engagement score updates
  trackEngagementScore(
    userId: string,
    score: number,
    properties: Record<string, unknown>
  ): void {
    this.posthog.capture("engagement_score_updated", {
      user_id: userId,
      engagement_score: score,
      score_tier: this.getScoreTier(score),
      ...properties,
    });

    // Update user properties
    this.posthog.identify(userId, {
      engagement_score: score,
      engagement_tier: this.getScoreTier(score),
      engagement_updated_at: new Date().toISOString(),
    });
  }

  private getScoreTier(score: number): string {
    if (score >= 500) return "champion";
    if (score >= 300) return "enthusiast";
    if (score >= 150) return "regular";
    if (score >= 50) return "casual";
    return "new";
  }
}

// Export helper instances
export const createAnalyticsHelpers = (posthog: PostHog) => ({
  segmentation: new UserSegmentationHelper(posthog),
  retention: new RetentionAnalyzer(posthog),
  engagement: new EngagementScorer(posthog),
});

// PostHog Dashboard Configuration Export
export const POSTHOG_DASHBOARD_CONFIG = {
  funnels: FUNNELS,
  cohorts: COHORTS,
  segments: USER_SEGMENTS,
  retentionEvents: [
    "user_signed_up",
    "profile_completed",
    "page_viewed",
    "map_session_started",
    "model_page_viewed",
    "3d_viewer_session_started",
  ],
  engagementEvents: [
    "page_viewed",
    "map_marker_clicked",
    "model_page_viewed",
    "3d_viewer_session_started",
    "3d_viewer_measurement_started",
    "map_search_query",
  ],
};
